import React, { useState, useEffect, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import FormErrorText from "../../../../../components/form/FormErrorText";
import { MerchantEmailVerificationFormData } from "../../types/merchantRegister.types";
import { OTPInput } from "./OTPInput";
import MerchantStepButtons from "../MerchantStepButtons";
import { useAuthSession } from "../../../shared";
import {
  useSendMerchantVerificationCode,
  useVerifyMerchantEmail,
} from "../../hooks/useMerchantRegistrationActions";
import { useNavigate } from "react-router-dom";
import { ResultCode } from "../../../../../lib/result-codes";
import { createMerchantVerificationSchema } from "../../schemas/merchantRegister.schemas";
import { MERCHANT_ONBOARDING_STAGES } from "../../../shared";
import { MERCHANT_REGISTRATION_PATHS } from "../../utils/merchantOnboarding";

export const Step2VerifyEmailForm: React.FC = () => {
  const { t } = useTranslation();
  const { clearSession, user, updateMerchantOnboarding } = useAuthSession();
  const navigate = useNavigate();

  const { execute: sentOtp, isLoading: isResending } = useSendMerchantVerificationCode();
  const { execute: verifyOtp, isLoading: isVerifying } = useVerifyMerchantEmail();

  const [remainingSeconds, setRemainingSeconds] = useState(60);

  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<MerchantEmailVerificationFormData>({
    resolver: zodResolver(createMerchantVerificationSchema(t)),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { email: "", otp: "" },
  });
  const otpValue = useWatch({ control, name: "otp" });

  useEffect(() => {
    if (otpValue && errors.otp) clearErrors("otp");
  }, [clearErrors, errors.otp, otpValue]);

  const fetchRemainingSeconds = useCallback(async () => {
    if (!user?.email) return;
    const remainingSeconds = await sentOtp(user?.email);
    if (remainingSeconds > 0) setRemainingSeconds(remainingSeconds);
  }, [sentOtp, user?.email]);

  useEffect(() => {
    void fetchRemainingSeconds();
  }, [fetchRemainingSeconds]);

  useEffect(() => {
    if (remainingSeconds <= 0) return;

    const timer = window.setInterval(() => {
      setRemainingSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [remainingSeconds]);

  const onResendCode = useCallback(async () => {
    if (!user?.email || remainingSeconds > 0 || isResending) return;

    await fetchRemainingSeconds();
  }, [fetchRemainingSeconds, isResending, remainingSeconds, user?.email]);

  const onBack = useCallback(() => {
    clearSession();
    navigate(MERCHANT_REGISTRATION_PATHS.account, { replace: true });
  }, [clearSession, navigate]);

  const onVerifyCode = useCallback(async () => {
    if (!user?.email || isVerifying) return;

    await handleSubmit(async (value) => {
      const response = await verifyOtp(user.email, value.otp);

      if (response?.code === ResultCode.Success) {
        updateMerchantOnboarding(MERCHANT_ONBOARDING_STAGES.businessDetails, false);
        navigate(MERCHANT_REGISTRATION_PATHS.emailVerified, { replace: true });
      }
    })();
  }, [handleSubmit, isVerifying, navigate, updateMerchantOnboarding, user?.email, verifyOtp]);

  return (
    <div className="w-full">
      <div className="flex flex-col items-center text-center">
        <h2 className="text-xl md:text-2xl font-bold text-black dark:text-white py-[50px] font-nunito">
          {t("merchantRegister.step2.title")}
        </h2>

        <p className="text-[#75757D] dark:text-gray-400 text-sm md:text-base font-nunito">
          {t("merchantRegister.step2.description")}
        </p>

        <p className="text-black dark:text-white font-bold text-sm md:text-base font-nunito mt-[30px] mb-[84px] break-all px-2">
          {user?.email || ""}
        </p>

        <div className="mb-[40px]">
          <Controller
            name="otp"
            control={control}
            render={({ field }) => (
              <OTPInput
                value={field.value ?? ""}
                onChange={field.onChange}
                disabled={false}
                error={!!errors.otp}
              />
            )}
          />
          <FormErrorText
            message={errors.otp?.message?.toString()}
            className="text-left text-[12px] font-semibold font-nunito mt-[7px]"
          />
        </div>

        <div className="w-full max-w-md bg-[#EEF9EF] dark:bg-[#0d2818] rounded-lg p-3 md:p-4 mb-4 md:mb-5 flex items-center justify-center gap-2">
          <div className="w-5 h-5 rounded-full bg-[#00C49A] flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
          <span className="text-[#00C49A] font-medium font-nunito text-xs md:text-sm">
            {t("merchantRegister.step2.codeSent")}
          </span>
        </div>

        <button
          type="button"
          onClick={onResendCode}
          disabled={isResending || remainingSeconds > 0}
          aria-disabled={isResending || remainingSeconds > 0}
          className={cn(
            "px-5 py-2.5 md:px-6 md:py-3 rounded-[27px] border border-[#DCDCDC] dark:border-gray-600",
            "text-black dark:text-white font-bold text-sm md:text-base font-nunito",
            "hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
            isResending && "opacity-50 cursor-not-allowed",
          )}
        >
          {isResending
            ? t("merchantRegister.step2.sending")
            : remainingSeconds > 0
              ? `${t("merchantRegister.step2.resendCode")} (${remainingSeconds}s)`
              : t("merchantRegister.step2.resendCode")}
        </button>
      </div>

      <div className="flex-1 flex items-end justify-end">
        <MerchantStepButtons
          previousBtn={{
            label: t("merchantRegister.buttons.back"),
            onClick: onBack,
          }}
          nextBtn={{
            label: t("merchantRegister.buttons.next"),
            onClick: onVerifyCode,
            loading: isVerifying,
          }}
        />
      </div>
    </div>
  );
};

export default Step2VerifyEmailForm;
